import client from './inventory-request';
import { withStoreCreateBody, withStoreUpdateBody, getStoreActorId } from '../hooks/useStoreScope';
import {
  toRfqApiItemBody,
  toRfqItemUpdateBody,
  lineNeedsUpdate,
  normalizeApiList,
  snapshotPortalItems,
  toEvaluateQuoteBody,
  quoteNeedsEvaluateBeforeAccept,
  quoteLifecycleStatus,
  isQuoteAlreadyAcceptedMessage,
} from '../utils/rfqEntity';
import { notifyInvitedVendorsOnPublish } from '../utils/rfqInviteNotify';

// RFQs — official spec #16–#28 (proxied via EMR /inventory/rfqs/...)

export const getRfqs = (p) => client.get('/inventory/rfqs/', p);
export const getRfq = (id) => client.get(`/inventory/rfqs/${id}/`);
export const createRfq = (body) => client.post('/inventory/rfqs/', withStoreCreateBody(body));
export const updateRfq = (id, body) => client.patch(`/inventory/rfqs/${id}/`, withStoreUpdateBody(body));

export const getRfqItems = (id) => client.get(`/inventory/rfqs/${id}/items/`);
export const addRfqItem = (rfqId, body) =>
  client.post(`/inventory/rfqs/${rfqId}/items/`, withStoreCreateBody(body));

// Flat rfq-items routes (not nested under /rfqs/{id}/)
export const getRfqItem = (itemId, scope = {}) =>
  client.get(`/inventory/rfq-items/${itemId}/`, scope);
export const updateRfqItem = (itemId, body) =>
  client.patch(`/inventory/rfq-items/${itemId}/`, withStoreUpdateBody(body));
export const deleteRfqItem = (itemId, scope = {}) =>
  client.delete(`/inventory/rfq-items/${itemId}/`, scope);

export const getRfqSummary = (id) => client.get(`/inventory/rfqs/${id}/summary/`);
export const publishRfq = (id) => client.post(`/inventory/rfqs/${id}/publish/`, {});
export const closeRfq = (id) => client.post(`/inventory/rfqs/${id}/close/`, {});
export const cancelRfq = (id) => client.post(`/inventory/rfqs/${id}/cancel/`, {});
/** The public vendor-portal link for this RFQ. Staff-only; the tenant is baked in server-side. */
export const getRfqPortalLink = (id) => client.get(`/inventory/rfqs/${id}/portal-link/`);

/** Server-side vendor invite email. Body is only vendor_ids. */
export const sendRfqVendorEmailInvite = (rfqId, vendorIds) =>
  client.post(`/inventory/rfqs/${rfqId}/vendor-email-invite/`, { vendor_ids: vendorIds });

export const getRfqClarifications = (id) =>
  client.get(`/inventory/rfqs/${id}/clarifications/`);

export const answerRfqClarification = (rfqId, clarifId, body) =>
  client.post(`/inventory/rfqs/${rfqId}/clarifications/${clarifId}/answer/`, withStoreUpdateBody(body));

/** Open a sealed RFQ's rates early — reason is mandatory and recorded server-side. */
export const unsealRfq = (id, reason) =>
  client.post(`/inventory/rfqs/${id}/unseal/`, { reason });

export const getRfqQuotes = (id) => client.get(`/inventory/rfqs/${id}/quotes/`);
export const getRfqQuoteComparison = (id) => client.get(`/inventory/rfqs/${id}/quote-comparison/`);
export const getRfqEvaluationSummary = (id) => client.get(`/inventory/rfqs/${id}/evaluation-summary/`);

/** Legacy fallback if backend still exposes flat rfq-vendors list */
export const getRfqVendors = (rfqIdOrParams) =>
  client.get('/inventory/rfq-vendors/', typeof rfqIdOrParams === 'object' ? rfqIdOrParams : { rfq_id: rfqIdOrParams });

/** Sync line items on DRAFT edit — PATCH existing, DELETE removed, POST new */
export async function syncRfqItems(rfqId, originalLines, editedLines, submissionDeadline) {
  const origList = originalLines || [];
  const editedList = (editedLines || []).filter((l) => l.item_id && l.required_quantity);
  const origById = new Map(origList.filter((l) => l.id).map((l) => [l.id, l]));
  const editedIds = new Set(editedList.filter((l) => l.id).map((l) => l.id));

  await Promise.all(
    origList
      .filter((l) => l.id && !editedIds.has(l.id))
      .map((l) => deleteRfqItem(l.id)),
  );

  await Promise.all(
    editedList.map(async (line) => {
      if (line.id) {
        const orig = origById.get(line.id);
        if (orig && lineNeedsUpdate(orig, line)) {
          await updateRfqItem(line.id, toRfqItemUpdateBody(line));
        }
        return;
      }
      await addRfqItem(rfqId, toRfqApiItemBody(line, submissionDeadline));
    }),
  );
}

export async function createRfqWithItemsAndVendors(formPayload, lines, { publish = false } = {}) {
  const rfq = await createRfq(formPayload);
  const rfqId = rfq?.id || rfq?.rfq_id;
  if (!rfqId) throw new Error('RFQ created but no ID returned');

  const validLines = (lines || []).filter((l) => l.item_id && l.required_quantity);
  if (validLines.length > 0) {
    await Promise.all(
      validLines.map((line) => addRfqItem(rfqId, toRfqApiItemBody(line, formPayload.submission_deadline))),
    );
  }

  await persistRfqBidFlags(rfqId, formPayload, validLines);

  if (publish) {
    const published = await publishRfq(rfqId);
    const notify = await notifyInvitedVendorsOnPublish(rfqId, {
      rfq: { ...formPayload, ...published },
      vendorIds: formPayload.rfq_payload?.invited_vendor_ids,
    }).catch((err) => ({ sent: [], failed: [{ reason: err.message }] }));
    return { ...rfq, ...published, id: rfqId, rfq_id: rfqId, notify };
  }
  return { ...rfq, id: rfqId, rfq_id: rfqId };
}

/** Bid flags and portal item snapshot — only while DRAFT. PATCH after publish 422s. */
async function persistRfqBidFlags(rfqId, formPayload = {}, lines) {
  const current = await getRfq(rfqId).catch(() => ({}));
  if (current?.status && current.status !== 'DRAFT') return;
  const payload = (current && current.rfq_payload && typeof current.rfq_payload === 'object')
    ? current.rfq_payload
    : {};
  const open = Boolean(formPayload.is_open_bid);
  const sealed = Boolean(formPayload.is_sealed_bid);
  const portal_items = snapshotPortalItems(lines);
  await updateRfq(rfqId, {
    is_open_bid: open,
    is_sealed_bid: sealed,
    allow_unregistered: open,
    rfq_payload: {
      ...payload,
      is_open_bid: open,
      is_sealed_bid: sealed,
      allow_unregistered: open,
      invite_only: !open,
      who_can_bid: open ? 'OPEN' : 'INVITED',
      ...(portal_items.length ? { portal_items } : {}),
    },
  });
}

export async function persistPortalItemSnapshots(rfqId, lines) {
  const portal_items = snapshotPortalItems(lines)
    .filter((row) => row.item_name && row.item_name !== '—');
  if (!rfqId || !portal_items.length) return;
  const current = await getRfq(rfqId).catch(() => ({}));
  const payload = (current && current.rfq_payload && typeof current.rfq_payload === 'object')
    ? current.rfq_payload
    : {};
  await updateRfq(rfqId, {
    rfq_payload: {
      ...payload,
      portal_items,
    },
  });
}

export async function updateRfqWithItemsAndVendors(
  rfqId,
  formPayload,
  lines,
  originalLines,
  { publish = false } = {},
) {
  await updateRfq(rfqId, formPayload);
  await syncRfqItems(rfqId, originalLines, lines, formPayload.submission_deadline);
  await persistRfqBidFlags(rfqId, formPayload, lines);

  if (publish) {
    const published = await publishRfq(rfqId);
    const notify = await notifyInvitedVendorsOnPublish(rfqId, {
      rfq: { ...formPayload, ...published },
      vendorIds: formPayload.rfq_payload?.invited_vendor_ids,
    }).catch((err) => ({ sent: [], failed: [{ reason: err.message }] }));
    return { ...published, id: rfqId, rfq_id: rfqId, notify };
  }
  const updated = await getRfq(rfqId);
  return { ...updated, id: rfqId, rfq_id: rfqId };
}

// Vendor quotes — Section 4 (vendor-bidding-service)
export const getVendorQuotes = (p) => client.get('/inventory/vendor-quotes/', p);
export const getVendorQuote = (id) => client.get(`/inventory/vendor-quotes/${id}/`);
export const createVendorQuote = (body) => client.post('/inventory/vendor-quotes/', withStoreCreateBody(body));
export const updateVendorQuote = (id, body) =>
  client.patch(`/inventory/vendor-quotes/${id}/`, withStoreUpdateBody(body));

export const addVendorQuoteItem = (quoteId, body) =>
  client.post(`/inventory/vendor-quotes/${quoteId}/items/`, withStoreCreateBody(body));
export const getVendorQuoteItemsByQuote = (quoteId) =>
  client.get(`/inventory/vendor-quotes/${quoteId}/items/`);

export const getVendorQuoteItems = (p) => client.get('/inventory/vendor-quote-items/', p);
export const createVendorQuoteItem = (body) =>
  client.post('/inventory/vendor-quote-items/', withStoreCreateBody(body));
export const updateVendorQuoteItem = (itemId, body) =>
  client.patch(`/inventory/vendor-quote-items/${itemId}/`, withStoreUpdateBody(body));
export const deleteVendorQuoteItem = (itemId) =>
  client.delete(`/inventory/vendor-quote-items/${itemId}/`);

export const submitVendorQuote = (id, body = {}) => {
  const actor = getStoreActorId();
  const payload = { ...body };
  if (actor && !payload.submitted_by) payload.submitted_by = actor;
  return client.post(`/inventory/vendor-quotes/${id}/submit/`, payload);
};

/** @deprecated use submitVendorQuote */
export const submitQuote = submitVendorQuote;

export const withdrawVendorQuote = (id, body = {}) =>
  client.post(`/inventory/vendor-quotes/${id}/withdraw/`, body);

export const startQuoteEvaluation = (id) =>
  client.post(`/inventory/vendor-quotes/${id}/start-evaluation/`, {});

export const evaluateVendorQuote = (id, body) =>
  client.post(`/inventory/vendor-quotes/${id}/evaluate/`, withStoreUpdateBody(body));

function isIgnorableEvalStepError(err) {
  const m = String(err?.message || '').toLowerCase();
  return /already|under_evaluation|evaluated|cannot start/.test(m);
}

/**
 * The accept API still requires quote status EVALUATED. Staff no longer scores
 * quotes, so award/split award mark the quote recommended first.
 */
export async function ensureQuoteEvaluatedForAward(quoteId) {
  const quote = await getVendorQuote(quoteId).catch(() => null);
  const status = quoteLifecycleStatus(quote);
  if (!quoteNeedsEvaluateBeforeAccept(status)) return;

  if (status !== 'UNDER_EVALUATION') {
    try {
      await startQuoteEvaluation(quoteId);
    } catch (e) {
      if (!isIgnorableEvalStepError(e)) {
        // Close often already moved the quote; evaluate next anyway.
      }
    }
  }

  try {
    await evaluateVendorQuote(quoteId, toEvaluateQuoteBody({
      technical_score: 100,
      commercial_score: 100,
      evaluation_status: 'RECOMMENDED',
    }));
  } catch (e) {
    if (!isIgnorableEvalStepError(e)) throw e;
  }
}

async function acceptedQuoteRecordFor(vendorQuoteId) {
  const payload = await getAcceptedQuotes({ vendor_quote_id: vendorQuoteId }).catch(() => []);
  const rows = Array.isArray(payload) ? payload : (payload?.items || payload?.results || []);
  return rows.find((row) => (
    row.vendor_quote_id === vendorQuoteId
    || row.quote_id === vendorQuoteId
    || row.vendor_quote === vendorQuoteId
  )) || rows[0] || null;
}

function toAcceptResult(vendorQuoteId, quote, acceptedRecord) {
  const acceptedId = acceptedRecord?.id || acceptedRecord?.accepted_quote_id || quote?.accepted_quote_id;
  return {
    ...(quote || {}),
    ...(acceptedRecord || {}),
    vendor_quote_id: vendorQuoteId,
    accepted_quote_id: acceptedId,
    id: acceptedId || quote?.id || vendorQuoteId,
    status: 'ACCEPTED',
  };
}

/** Accept quote — POST /vendor-quotes/{id}/accept (NOT /accepted-quotes/) */
export const acceptVendorQuote = async (quoteId, body, scope = {}) => {
  const existing = await getVendorQuote(quoteId).catch(() => null);
  if (quoteLifecycleStatus(existing) === 'ACCEPTED') {
    const record = await acceptedQuoteRecordFor(quoteId);
    return toAcceptResult(quoteId, existing, record);
  }

  await ensureQuoteEvaluatedForAward(quoteId);

  try {
    const accepted = await client.post(
      `/inventory/vendor-quotes/${quoteId}/accept/`,
      withStoreUpdateBody(body),
      scope,
    );
    const record = await acceptedQuoteRecordFor(quoteId);
    return toAcceptResult(quoteId, accepted, record || accepted);
  } catch (e) {
    if (isQuoteAlreadyAcceptedMessage(e.message)) {
      const quote = await getVendorQuote(quoteId).catch(() => existing);
      const record = await acceptedQuoteRecordFor(quoteId);
      return toAcceptResult(quoteId, quote, record);
    }
    throw e;
  }
};

/** @deprecated use acceptVendorQuote */
export const acceptQuote = acceptVendorQuote;

export const getQuoteEvaluation = (p) => client.get('/inventory/quote-evaluation/', p);
export const getAcceptedQuotes = (p) => client.get('/inventory/accepted-quotes/', p);
export const createAcceptedQuote = (body) =>
  client.post('/inventory/accepted-quotes/', withStoreCreateBody(body));
export const updateAcceptedQuote = (id, body) =>
  client.patch(`/inventory/accepted-quotes/${id}/`, withStoreUpdateBody(body));

export const markConvertedToPo = (acceptedQuoteId, scope = {}) =>
  client.post(`/inventory/accepted-quotes/${acceptedQuoteId}/mark-converted-to-po/`, {}, scope);

export { getStoreActorId };
